import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Badge — MOMENTUM tag primitive.
 *
 * What changed vs shadcn defaults:
 *   - Default weight: font-semibold → font-medium. Semibold read heavy
 *     when stacked (workout cards often render 3-5 badges in a row).
 *   - Hover bg shift: /80 → /90. Less of a jump, matches the button-polish
 *     pass (PR #179).
 *   - Semantic variants added: `success`, `warning`, `info`, `volt`.
 *     Previously callers were writing `<Badge variant="default"
 *     className="bg-success text-success-foreground">` to fake these —
 *     direct support makes that ergonomic.
 *   - `outline` is now token-driven (border-border) — was border-default
 *     which cascaded to whatever inherited color happened to be set.
 *   - New `sm` size: tighter padding + 10px text for inline-row use
 *     (was being hand-rolled as `text-[10px] px-1.5 py-0` in many places).
 *
 * The default rounded-full geometry is preserved — pill shape matches
 * the way Tessa visually parses badges as "labels," not "cards."
 */
const badgeVariants = cva(
  "inline-flex items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border-border text-foreground bg-transparent",
        // Semantic — paired with token colors from index.css so they
        // theme with the rest of the app.
        success:
          "border-transparent bg-success/15 text-success hover:bg-success/20",
        warning:
          "border-transparent bg-warning/15 text-warning hover:bg-warning/20",
        info:
          "border-transparent bg-info/15 text-info hover:bg-info/20",
        // Volt — celebratory accent. Use sparingly; same energy as the
        // button system's volt variant.
        volt:
          "border-transparent bg-primary/15 text-foreground hover:bg-primary/20",
      },
      size: {
        default: "px-2.5 py-0.5 text-xs font-medium tracking-tight",
        sm: "px-1.5 py-0 text-[10px] font-medium tracking-tight h-4",
        lg: "px-3 py-1 text-sm font-medium tracking-tight",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, size, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant, size }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
